import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiHome, FiMap, FiBookOpen, FiClipboard, FiAward, FiZap, FiEdit3, FiPlayCircle, FiMessageCircle } from 'react-icons/fi';
import { VscGame } from 'react-icons/vsc';
import DuelIcon from '@/components/custom/icons/DuelIcon';
import { cn } from '@/lib/utils';
import { useSession } from '@/hooks/useSession';
import { PATHS } from '@/routes/paths';
import tiaAvatar from '@/assets/images/tia/tia-avatar-3d.png';
import { TiaChatBar } from './tia-chat-bar';
import { TiaChatSheet } from './tia-chat-sheet';
import { useTiaChat, getUserLearningPathIdFromPathname } from './use-tia-chat';
import { usePlatformAbilities } from '@/hooks/usePlatformAbilities';

export function getActiveKey(pathname) {
  if (pathname === '/') return 'inicio';
  if (pathname.startsWith('/trilha')) return 'trilha';
  if (pathname.startsWith('/desafio-da-semana')) return 'desafio-semana';
  if (pathname.startsWith('/duelos')) return 'duelos';
  if (pathname.startsWith('/curso')) return 'cursos';
  if (pathname.startsWith('/redacao')) return 'redacao';
  if (pathname.startsWith('/simulados')) return 'simulado';
  if (pathname.startsWith('/usuario/medalhas')) return 'conquistas';
  if (pathname.startsWith('/desafio-ao-vivo')) return 'desafios';
  if (pathname.startsWith('/aulas-ao-vivo')) return 'aulas-ao-vivo';
  if (pathname.startsWith('/feed')) return 'forum';
  return null;
}

export function buildTabs(userId, abilities) {
  return [
    { key: 'inicio', label: 'Início', to: PATHS.DASHBOARD.ROOT, Icon: FiHome, visibility: true },
    { key: 'trilha', label: 'Trilha', to: PATHS.LEARNING_PATH.ROOT, Icon: FiMap, visibility: !!abilities?.['plano-de-estudo'] },
    { key: 'desafio-semana', label: 'Desafios', to: PATHS.WEEK_CHALLENGE.LIST, Icon: VscGame, visibility: !!abilities?.['desafio-da-semana'] },
    { key: 'duelos', label: 'Duelos', to: PATHS.DUELS.LIST, Icon: DuelIcon, visibility: !!abilities?.duelos },
    { key: 'cursos', label: 'Cursos', to: PATHS.COURSE.LIST, Icon: FiBookOpen, visibility: !!abilities?.cursos },
    { key: 'redacao', label: 'Redação', to: PATHS.ESSAY.LIST, Icon: FiEdit3, visibility: !!abilities?.redacao },
    { key: 'simulado', label: 'Simulado', to: PATHS.EXAM.MOCK_LIST, Icon: FiClipboard, visibility: !!abilities?.['avaliacoes-simulados'] },
    { key: 'conquistas', label: 'Conquistas', to: PATHS.PROFILE.USER('medalhas', userId), Icon: FiAward, visibility: true },
    { key: 'desafios', label: 'Ao vivo', to: PATHS.LIVE_CHALLENGE.JOIN, Icon: FiZap, visibility: !!abilities?.['desafio-ao-vivo'] },
    { key: 'aulas-ao-vivo', label: 'Aula ao vivo', to: PATHS.LIVE_CLASSES.LIST, Icon: FiPlayCircle, visibility: !!abilities?.['aulas-ao-vivo'] },
    { key: 'forum', label: 'Fórum', to: PATHS.FEED.LIST, Icon: FiMessageCircle, visibility: !!abilities?.feed }
  ];
}

const EXIT_ANIMATION_MS = 180; // espelha tia-bar-out/tia-sheet-out no sass

// Mantém o elemento montado durante a animação de saída antes de desmontar.
function usePresence(open, exitMs = EXIT_ANIMATION_MS) {
  const [shouldRender, setShouldRender] = React.useState(open);

  React.useEffect(() => {
    if (open) {
      setShouldRender(true);
      return undefined;
    }
    const timer = setTimeout(() => setShouldRender(false), exitMs);
    return () => clearTimeout(timer);
  }, [open, exitMs]);

  return { shouldRender, closing: !open && shouldRender };
}

export function BottomBarInfantil() {
  const { session } = useSession();
  const { pathname } = useLocation();
  const { abilities } = usePlatformAbilities();
  const userId = session?.user?.id;
  const activeKey = getActiveKey(pathname);
  const tabs = buildTabs(userId, abilities).filter(tab => tab.visibility);
  const tabsMiddle = Math.ceil(tabs.length / 2);

  const [chatOpen, setChatOpen] = React.useState(false);
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const barPresence = usePresence(chatOpen);
  const sheetPresence = usePresence(sheetOpen);
  const chat = useTiaChat({ userLearningPathId: getUserLearningPathIdFromPathname(pathname) });
  const hasLoadedHistory = React.useRef(false);

  const closeChat = () => {
    setChatOpen(false);
    setSheetOpen(false);
  };

  const openChat = () => {
    setChatOpen(open => {
      const next = !open;
      if (!next) setSheetOpen(false);
      return next;
    });
    if (!hasLoadedHistory.current) {
      hasLoadedHistory.current = true;
      chat.loadHistory();
    }
  };

  const renderTab = ({ key, label, to, Icon }) => {
    const isActive = activeKey === key;
    return (
      <Link
        key={key}
        to={to}
        aria-current={isActive ? 'page' : undefined}
        className={cn('flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-full px-1 py-1.5 text-center text-[11px] font-semibold transition-colors max-[430px]:text-[10px]', isActive ? 'text-secondary' : 'text-neutral-dark')}
        style={isActive ? { background: 'color-mix(in srgb, var(--color-secondary) 16%, #fff)' } : undefined}
      >
        <Icon
          size={20}
          aria-hidden="true"
        />
        <span className="leading-none">{label}</span>
      </Link>
    );
  };

  return (
    <>
      {sheetPresence.shouldRender && (
        <TiaChatSheet
          messages={chat.messages}
          status={chat.status}
          onRetry={chat.retry}
          onClose={() => setSheetOpen(false)}
          closing={sheetPresence.closing}
        />
      )}
      <div className="fixed bottom-[calc(1.75rem+env(safe-area-inset-bottom))] left-1/2 z-[103] w-[calc(100%-1rem)] max-w-xl -translate-x-1/2 sm:w-[calc(100%-2rem)] md:max-w-[70vw]">
        <nav
          className={cn('c-bottom-bar-infantil', 'flex items-stretch', 'rounded-full border border-neutral-lighter bg-white px-2 py-1.5', 'shadow-[0_12px_32px_rgba(0,0,0,0.16)]')}
          aria-label="Navegação principal"
        >
          {tabs.slice(0, tabsMiddle).map(renderTab)}
          {abilities?.['tia-chat'] && (
            <button
              type="button"
              onClick={openChat}
              aria-label="Conversar com a T.I.A"
              aria-expanded={chatOpen}
              aria-controls="tia-chat-sheet"
              // A raia estreita no mobile libera largura pros labels; o avatar
              // (shrink-0, flutuando acima da barra) transborda sem colidir.
              className="flex w-10 flex-none items-start justify-center sm:w-auto sm:flex-1"
            >
              <img
                src={tiaAvatar}
                alt=""
                className={cn('-mt-8 h-16 w-16 shrink-0 rounded-full border-4 border-white bg-white shadow-lg', 'transition-transform active:scale-95', chatOpen && 'ring-2 ring-secondary')}
              />
            </button>
          )}
          {tabs.slice(tabsMiddle).map(renderTab)}
        </nav>
        {barPresence.shouldRender && (
          <TiaChatBar
            onSubmit={body => {
              setSheetOpen(true);
              chat.send(body);
            }}
            onFocus={() => {
              if (chat.messages.length > 0) setSheetOpen(true);
            }}
            onClose={closeChat}
            disabled={chat.status === 'thinking' || chat.status === 'loading'}
            closing={barPresence.closing}
          />
        )}
      </div>
    </>
  );
}
